<template>
    <Form as="" :validation-schema="schema" v-slot="{ meta, handleSubmit }">
        <bs5-base-modal :title="$t('account.title')" :visible="visible" @close="closeModal">
            <template v-slot:default>
                <div v-if="errorMessage" class="au-alert-error" role="alert">
                    <i class="bi bi-exclamation-circle-fill"></i>{{ errorMessage }}
                </div>
                <form novalidate @submit.prevent="handleSubmit($event, changePassword)">
                    <bs5-form-field id="currentPassword" name="oldPassword" type="password" icon="bi-lock"
                        autocomplete="current-password" :label="$t('account.currentPassword')"
                        v-model="form.oldPassword" />
                    <bs5-form-field id="newPassword" name="newPassword" type="password" icon="bi-key"
                        autocomplete="new-password" :label="$t('account.newPassword')" :hint="$t('account.policy')"
                        v-model="form.newPassword" />
                    <bs5-form-field id="confirmNewPassword" name="confirmNewPassword" type="password" icon="bi-key"
                        autocomplete="new-password" :label="$t('account.confirmPassword')"
                        v-model="form.confirmNewPassword" />
                    <button type="submit" class="d-none" tabindex="-1" aria-hidden="true"></button>
                </form>
            </template>

            <template v-slot:footer>
                <bs5-cancel-button @click="closeModal" />
                <bs5-save-button :loading="saving" :disabled="!meta.valid" @click="handleSubmit($event, changePassword)">
                    <i class="bi bi-check2"></i>{{ $t("account.changePassword") }}
                </bs5-save-button>
            </template>
        </bs5-base-modal>
    </Form>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { Form } from "vee-validate";
import * as yup from "yup";
import { t } from "@/helpers/i18n";
import userService from "@/services/user.service";
import { showToast } from "@/utils/toast";
import { newPassword, passwordConfirmation, requiredPassword } from "@/utils/validation";

// ExceptionMiddleware serializuje błąd Newtonsoftem bez camelCase, więc pole nazywa się "Message"
type ApiFailure = { status?: number; data?: { Message?: string; message?: string } };

const emptyForm = () => ({ oldPassword: "", newPassword: "", confirmNewPassword: "" });

export default defineComponent({
    name: "ChangeOwnPasswordModal",
    components: { Form },
    props: {
        visible: { type: Boolean, required: true },
    },
    emits: ["close"],
    data() {
        return {
            form: emptyForm(),
            saving: false,
            errorMessage: "",
        };
    },
    computed: {
        schema() {
            return yup.object({
                oldPassword: requiredPassword(),
                newPassword: newPassword(),
                confirmNewPassword: passwordConfirmation("newPassword"),
            });
        },
    },
    watch: {
        visible(isVisible: boolean) {
            if (isVisible) {
                this.form = emptyForm();
                this.errorMessage = "";
            }
        },
    },
    methods: {
        async changePassword() {
            this.errorMessage = "";
            this.saving = true;
            try {
                await userService.changeOwnPassword(this.form.oldPassword, this.form.newPassword, this.form.confirmNewPassword);
                showToast(t("account.changed"), "success");
                this.closeModal();
            } catch (error) {
                this.errorMessage = this.describeFailure(error as ApiFailure);
            } finally {
                this.saving = false;
            }
        },
        describeFailure(failure: ApiFailure): string {
            // API zwraca 422 z kodami błędów ASP.NET Identity oddzielonymi przecinkami
            const message = failure?.data?.Message ?? failure?.data?.message ?? "";
            const codes = failure?.status === 422 ? message.split(",") : [];
            if (codes.includes("PasswordMismatch")) {
                return t("account.wrongCurrent");
            }
            if (codes.includes("PasswordConfirmationMismatch")) {
                return t("account.mismatch");
            }
            if (codes.some(code => code === "PasswordTooShort" || code.startsWith("PasswordRequires"))) {
                return t("account.weak");
            }
            return t("account.failed");
        },
        closeModal() {
            this.$emit("close");
        },
    },
});
</script>
